'use client';

import { RatingStars } from '@eliadmin/core/src/components/dlz/ui/rating-stars';
import { useProductRating } from '@eliadmin/core/src/components/dlz/product-detail/buy-box/use-product-rating';
import { showProductTab } from '@eliadmin/core/src/components/dlz/product-detail/anchors';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { normalizeIntlLocale } from '@eliadmin/core/src/utils/intl-utils';

/** Slovak plural of „hodnotenie“: 1 · 2–4 · other. */
function reviewsKey(count: number) {
  if (count === 1) return 'text3511';
  return count >= 2 && count <= 4 ? 'text3512' : 'text3513';
}

/** Figma „Heading“: manufacturer overline, H1, stars + rating + reviews link + product code. */
export function BuyBoxHeading({ titleId }: { titleId: string }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const rating = useProductRating();

  const brand = product.manufacturers?.[0]?.name;
  const code = selectedVariant?.code || product.code;
  const average = rating
    ? new Intl.NumberFormat(normalizeIntlLocale(locale), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(rating.average)
    : '';

  return (
    <header className="flex flex-col gap-3">
      {brand ? (
        <p className="dlz-label text-[#6B7079]">
          <span className="dlz-trim block">{brand}</span>
        </p>
      ) : null}
      <h1 id={titleId} data-ea-primary-heading className="dlz-h2 break-words text-[#0F1012]">
        {selectedVariant?.name || product.name}
      </h1>
      {rating || code ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {rating ? (
            <div className="flex items-center gap-3">
              <RatingStars value={rating.average} label={`${t('text587')} ${average} / 5`} />
              <span className="dlz-body-s-medium text-[#0F1012]">{average}</span>
              <a
                href="#hodnotenia"
                onClick={event => {
                  event.preventDefault();
                  showProductTab('hodnotenia');
                }}
                className="dlz-body-s rounded-sm text-[#D7191F] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
              >
                {rating.count} {t(reviewsKey(rating.count))}
              </a>
            </div>
          ) : null}
          {rating && code ? (
            <span aria-hidden="true" className="hidden size-1 rounded-full bg-[#E2E4E7] sm:block" />
          ) : null}
          {code ? (
            <span className="dlz-body-s text-[#6B7079]">
              {t('text19')}: {code}
            </span>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
